import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Users, RefreshCw, Package } from "lucide-react";

const IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80";

const FEATURES = [
  {
    icon: Sparkles,
    title: "Fast & Compliant Business Setup",
    copy: "We handle your UAE company formation from start to finish — meeting every deadline and ensuring full compliance with DED, freezone authorities and UAE federal regulations.",
  },
  {
    icon: Users,
    title: "100% Satisfied Customer Service",
    copy: "Our team offers unlimited support and guidance, always available to answer your questions. You can rely on us to handle your financial needs with precision and accuracy.",
  },
  {
    icon: RefreshCw,
    title: "Always Up to Date",
    copy: "UAE business regulations change regularly. We keep you informed on the latest updates — from corporate tax requirements to freezone policy changes — so your business stays compliant.",
  },
  {
    icon: Package,
    title: "One-Stop Business Setup Solutions",
    copy: "From company registration and visa processing to accounting and PRO services, StartINC provides everything your business needs under one roof — fully customised to your goals.",
  },
];

export default function JourneyV2() {
  return (
    <section
      data-testid="v2-journey"
      className="relative bg-[#001438] text-white py-10 md:py-14 overflow-hidden"
    >
      <div
        className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#3366FF]/50 to-transparent"
        aria-hidden
      />
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-12">
          <div className="lg:col-span-7">
            <p className="overline text-[#6B8AFF]">Our approach</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
              A new generation of UAE{" "}
              <span className="font-black text-[#6B8AFF]">business setup</span> partners.
            </h2>
            <div className="mt-8 space-y-5 text-slate-300/85 leading-relaxed max-w-2xl">
              <p>
                StartINC was founded by operators who have lived UAE incorporation
                from every angle — government desks, corporate banking compliance,
                and the founder's chair. We built the firm we wished we had when
                we set up our first companies in Dubai.
              </p>
              <p>
                We offer end-to-end solutions — from company formation and
                corporate banking to accounting, tax and PRO services —
                empowering entrepreneurs to launch in the UAE without the friction
                that has historically defined this market.
              </p>
              <p>
                What sets us apart? Senior advisors on every file, written
                transparent quotes, weekly status reports and SLA-backed
                turnarounds. No call centres, no surprise fees, no shifting
                scope. Just a disciplined launchpad for your next chapter.
              </p>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative rounded-sm overflow-hidden border border-white/10">
              <img
                src={IMG}
                alt="Business setup"
                className="w-full h-[320px] md:h-[400px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#001438] via-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="chip-dark">A new generation</div>
                <p className="mt-3 font-heading font-light text-white text-xl leading-tight">
                  Built by operators —{" "}
                  <span className="font-black text-[#6B8AFF]">designed for founders</span>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
          {FEATURES.map(({ icon: Icon, title, copy }, i) => (
            <motion.div
              key={title}
              data-testid={`v2-feature-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#001438] p-8 md:p-10 hover:bg-[#001A4D] transition-colors"
            >
              <div className="w-12 h-12 rounded-sm border border-white/15 text-[#6B8AFF] inline-flex items-center justify-center">
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <h3 className="mt-6 font-heading font-semibold text-lg tracking-tight">
                {title}
              </h3>
              <p className="mt-2 text-sm text-slate-300/80 leading-relaxed">{copy}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
